import * as React from "react";
import { createPortal } from "react-dom";
import { Activity, ArrowRight, Cloud, FileCheck, Shield, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
type Solution = {
  title: string;
  description: string;
  label: string;
  icon: React.ComponentType<{
    className?: string;
    style?: React.CSSProperties;
    strokeWidth?: number;
  }>;
  technicalOverview: string;
  organizationalValue: string;
};
const solutions: Solution[] = [{
  title: "Cybersecurity and GRC",
  description: "Practical cybersecurity, governance, risk and compliance services for complex and regulated environments.",
  icon: Shield,
  label: "SECURITY AND GOVERNANCE",
  technicalOverview: "Implementation of robust GRC frameworks aligned with ISO/IEC 27001, NIST, and UAE Information Assurance (UAE IA), issued by the UAE Cybersecurity Council (CSC), supported by automated risk assessment tools and continuous compliance monitoring.",
  organizationalValue: "Reduces regulatory friction, minimizes operational risk, and provides executive leadership with clear visibility into the organization’s security posture through data-driven reporting."
}, {
  title: "API Security and Management",
  description: "Secure API management and integration capabilities supporting connected enterprise services.",
  icon: FileCheck,
  label: "DIGITAL PLATFORMS",
  technicalOverview: "Deployment of secure API gateways, OAuth 2.0 and OpenID Connect authentication flows, API discovery, traffic monitoring, threat protection, and integration controls across connected digital services.",
  organizationalValue: "Enables secure digital ecosystem growth, accelerates partner and application integrations, improves service delivery, and protects sensitive information exchanged through APIs."
}, {
  title: "Threat Intelligence and Security Operations",
  description: "Operational security and threat intelligence capabilities supporting informed cyber risk decisions.",
  icon: Activity,
  label: "SECURITY OPERATIONS",
  technicalOverview: "Design and implementation of security operations capabilities using SIEM, SOAR, EDR and XDR technologies, integrated threat intelligence, automated detection use cases, and structured incident response workflows.",
  organizationalValue: "Shortens detection and response times, improves visibility across the technology environment, supports informed cyber-risk decisions, and strengthens operational resilience."
}, {
  title: "Infrastructure and Digital Transformation",
  description: "Secure infrastructure, cloud and modernization solutions designed to support business resilience.",
  icon: Cloud,
  label: "INFRASTRUCTURE",
  technicalOverview: "Implementation of secure infrastructure architectures, zero-trust principles, cloud migration, network modernization, resilient platforms, and technology integration aligned with business and security requirements.",
  organizationalValue: "Increases operational agility, reduces infrastructure complexity, supports scalable growth, and builds a secure and resilient foundation for digital transformation."
}];
export default function CoreExpertiseSolutions() {
  const [openIndex, setOpenIndex] = React.useState<number | null>(null);
  const activeSolution = openIndex !== null ? solutions[openIndex] ?? null : null;
  React.useEffect(() => {
    if (openIndex === null) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpenIndex(null);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [openIndex]);
  return <>
      <section id="core-expertise-solutions" data-section-id="223689" className="overflow-x-clip bg-[#0B1220] px-6 py-20 md:px-10 md:py-24">
        <div className="mx-auto w-full max-w-7xl">
          <div className="mb-14 max-w-3xl md:mb-16">
            <span className="mb-4 block font-mono text-sm font-semibold uppercase tracking-widest" style={{
            color: "#13B8C8"
          }}>
              OUR CAPABILITIES
            </span>

            <h2 className="mb-6 text-4xl font-bold leading-tight tracking-tight text-white md:text-5xl lg:text-6xl">
              Cybersecurity and Technology{" "}
              <span className="text-white">Solutions</span>
            </h2>

            <p className="max-w-3xl text-lg leading-relaxed text-white/85">
              Integrated advisory and technology services designed to protect
              critical operations, strengthen governance and enable secure
              modernization.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {solutions.map((solution, index) => {
            const Icon = solution.icon;
            return <Card key={solution.title} data-index={index} className="group flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-[#26364D] bg-[#142033] transition duration-300 hover:-translate-y-2 hover:border-[#13B8C8] hover:shadow-2xl">
                  <CardContent className="flex h-full flex-col p-8">
                    <div className="mb-7 flex size-14 items-center justify-center rounded-xl" style={{
                  backgroundColor: "rgba(19, 184, 200, 0.12)"
                }}>
                      <Icon className="size-7 transition-transform duration-300 group-hover:scale-110" style={{
                    color: "#13B8C8"
                  }} strokeWidth={1.6} />
                    </div>

                    <span className="mb-4 block font-mono text-[11px] font-bold uppercase leading-relaxed tracking-widest text-white/65">
                      {solution.label}
                    </span>

                    <h3 className="mb-5 text-2xl font-semibold leading-tight tracking-tight text-white">
                      {solution.title}
                    </h3>

                    <p className="text-base leading-relaxed text-white/80">
                      {solution.description}
                    </p>

                    <div className="mt-auto pt-8">
                      <Button type="button" variant="ghost" onClick={() => setOpenIndex(index)} aria-label={`Learn more about ${solution.title}`} className="group/link h-auto p-0 text-sm font-semibold hover:bg-transparent" style={{
                    color: "#13B8C8"
                  }}>
                        <span className="relative">
                          Learn More
                          <span className="absolute -bottom-1 left-0 h-0.5 w-0 transition-all duration-300 group-hover/link:w-full" style={{
                        backgroundColor: "#13B8C8"
                      }} />
                        </span>

                        <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </section>

      {activeSolution && typeof document !== "undefined" && createPortal(<div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm md:p-8" onMouseDown={event => {
      if (event.currentTarget === event.target) {
        setOpenIndex(null);
      }
    }}>
            <div role="dialog" aria-modal="true" aria-labelledby="service-modal-title" className="relative max-h-[85vh] w-[calc(100vw-32px)] max-w-[960px] overflow-x-hidden overflow-y-auto rounded-2xl border border-[#13B8C8] bg-[#142033] p-6 shadow-2xl md:w-[calc(100vw-64px)] md:p-10" onMouseDown={event => event.stopPropagation()}>
              <Button type="button" variant="ghost" aria-label="Close service details" onClick={() => setOpenIndex(null)} className="absolute right-4 top-4 flex size-11 items-center justify-center rounded-lg text-white/65 transition hover:bg-white/10 hover:text-white md:right-6 md:top-6">
                <X className="size-6" />
              </Button>

              <h3 id="service-modal-title" className="mb-8 max-w-[calc(100%-64px)] pr-12 text-3xl font-bold leading-tight tracking-tight text-white md:text-4xl">
                {activeSolution.title}
              </h3>

              <div className="grid w-full grid-cols-1 gap-8 md:grid-cols-2 md:gap-12">
                <p className="m-0 min-w-0 break-normal text-left text-base leading-[1.75] tracking-normal text-white/90">
                  {activeSolution.technicalOverview}
                </p>

                <p className="m-0 min-w-0 break-normal text-left text-base leading-[1.75] tracking-normal text-white/90">
                  {activeSolution.organizationalValue}
                </p>
              </div>
            </div>
          </div>, document.body)}
    </>;
}